import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { PLATFORM_STYLE } from "@/lib/agents";
import { Loader2, Trash2, CheckCircle2, Calendar as CalIcon } from "lucide-react";

const STATUS_STYLE = {
  rascunho: "bg-slate-800 text-slate-300 border-slate-700",
  agendado: "bg-amber-950/50 text-amber-300 border-amber-700/50",
  publicado: "bg-emerald-950/50 text-emerald-300 border-emerald-700/50",
};

export default function CalendarView({ brand }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fPlat, setFPlat] = useState("all");
  const [fStatus, setFStatus] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/calendar", { params: brand ? { brand_id: brand.brand_id } : {} });
      setEntries(res.data);
    } catch (e) { toast.error("Erro ao carregar calendário."); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [brand?.brand_id]);

  const setStatus = async (id, status) => {
    await api.put(`/calendar/${id}/status`, { status });
    setEntries((e) => e.map((x) => (x.entry_id === id ? { ...x, status } : x)));
    toast.success("Status atualizado.");
  };
  const remove = async (id) => {
    await api.delete(`/calendar/${id}`);
    setEntries((e) => e.filter((x) => x.entry_id !== id));
    toast.success("Removido do calendário.");
  };

  const filtered = entries.filter((e) =>
    (fPlat === "all" || e.plataforma === fPlat) && (fStatus === "all" || e.status === fStatus));

  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <p className="overline text-emerald-400 mb-1">Etapa 8 — Calendário editorial</p>
          <h2 className="font-display text-2xl font-bold tracking-tight">Agenda de conteúdos</h2>
          <p className="text-sm text-slate-400 mt-1">Dia · Plataforma · Formato · Tema · Hook · CTA · Objetivo</p>
        </div>
        <div className="flex gap-3">
          <Select value={fPlat} onValueChange={setFPlat}>
            <SelectTrigger data-testid="filter-platform" className="w-[150px] bg-slate-900/60 border-slate-700"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">
              <SelectItem value="all">Todas as redes</SelectItem>
              <SelectItem value="Instagram">Instagram</SelectItem>
              <SelectItem value="TikTok">TikTok</SelectItem>
              <SelectItem value="Facebook">Facebook</SelectItem>
            </SelectContent>
          </Select>
          <Select value={fStatus} onValueChange={setFStatus}>
            <SelectTrigger data-testid="filter-status" className="w-[150px] bg-slate-900/60 border-slate-700"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">
              <SelectItem value="all">Todos os status</SelectItem>
              <SelectItem value="rascunho">Rascunho</SelectItem>
              <SelectItem value="agendado">Agendado</SelectItem>
              <SelectItem value="publicado">Publicado</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-emerald-400" /></div>
      ) : filtered.length === 0 ? (
        <div className="glass rounded-2xl p-10 text-center text-slate-400">
          <CalIcon className="w-10 h-10 mx-auto mb-3 text-slate-600" />
          Nenhum conteúdo agendado. Gere um pacote e clique em "Agendar no calendário".
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((e) => {
            const st = PLATFORM_STYLE[e.plataforma] || PLATFORM_STYLE.Instagram;
            return (
              <div key={e.entry_id} data-testid={`calendar-entry-${e.entry_id}`}
                className="glass rounded-xl p-4 flex flex-col sm:flex-row sm:items-center gap-3">
                <div className="font-mono text-sm text-emerald-300 w-28 shrink-0">
                  {new Date(e.dia + "T00:00:00").toLocaleDateString("pt-BR", { weekday: "short", day: "2-digit", month: "2-digit" })}
                  {e.hora && <span className="block text-xs text-slate-500">{e.hora}</span>}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <Badge className={`${st.badge} border`}>{e.plataforma}</Badge>
                    <Badge variant="outline" className="border-slate-700 text-slate-300">{e.formato}</Badge>
                    {!brand && <span className="text-xs text-slate-500">{e.brand_nome}</span>}
                  </div>
                  <p className="text-sm font-medium truncate">{e.tema}</p>
                  {e.hook && <p className="text-xs text-slate-400 truncate">🪝 {e.hook}</p>}
                </div>
                <div className="flex items-center gap-2">
                  <Select value={e.status} onValueChange={(v) => setStatus(e.entry_id, v)}>
                    <SelectTrigger data-testid={`status-${e.entry_id}`} className={`w-[130px] h-8 text-xs border ${STATUS_STYLE[e.status]}`}><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">
                      <SelectItem value="rascunho">Rascunho</SelectItem>
                      <SelectItem value="agendado">Agendado</SelectItem>
                      <SelectItem value="publicado">Publicado</SelectItem>
                    </SelectContent>
                  </Select>
                  <button onClick={() => remove(e.entry_id)} data-testid={`del-cal-${e.entry_id}`}
                    className="text-slate-500 hover:text-red-400 transition-colors p-1"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
